import React from "react";
import {
  BACKUPS,
  CONFIGURATIONS,
  OTA_HISTORY_LIST as cssClassName,
  VERSIONS,
} from "../../../cloud-connect/ota-update/constants";
import OTAHistoryTableHeader from "./ota-history-table-header";
import styles from "./ota-history-list.module.scss";
import OtaFirmwareStart from "../ota-firmware-start/ota-firmware-start";
import OtaConfigurationStart from "../ota-configuration-start/ota-configuration-start";
import OtaBackupStart from "../ota-backup-start/ota-backup-start";
import { ListItem } from "@src/types/ota-update";
import { formatBytes } from "@src/cloud-connect/utils/format-bytes";
import { TimezoneDate } from "@src/components/atoms/timezone-date/timezone-date";
import { useOtaHistoryList } from "./use-ota-history-list";
import { useSelector } from "react-redux";
import { RootState } from "@src/reducers/store";

interface OTAHistoryListProps {
  type: "versions" | "backups" | "configurations" | "configuration-backups";
  setIsModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedRow: React.Dispatch<React.SetStateAction<ListItem | null>>;
  list: ListItem[] | [];
  activeOTAHistoryTab: string;
  showingAnyBlock: boolean;
}

export const OTAHistoryList = ({
  type,
  setIsModalOpen,
  setSelectedRow,
  list,
  activeOTAHistoryTab,
  showingAnyBlock,
}: OTAHistoryListProps) => {
  const {
    tableRef,
    columnWidths,
    resizing,
    sortedRows,
    sortConfig,
    handleSort,
    handleMouseDown,
    handleRowClick,
    columns,
  } = useOtaHistoryList({
    list,
    setSelectedRow,
    setIsModalOpen,
  });
  const otaActiveView = useSelector(
    (state: RootState) => state.yourSlice?.otaActiveView,
  );

  const emptyStateMap = {
    [VERSIONS]: <OtaFirmwareStart />,
    [CONFIGURATIONS]: <OtaConfigurationStart />,
    [BACKUPS]: <OtaBackupStart otaActiveView={otaActiveView} />,
  };

  const renderCellContent = (columnKey: keyof ListItem, row: ListItem) => {
    if (columnKey === "uploader") {
      return row?.uploader?.display_name;
    }

    if (columnKey === "size_bytes") {
      return formatBytes(row?.size_bytes);
    }

    if (columnKey === "uploaded_at") {
      return row.uploaded_at ? <TimezoneDate date={row.uploaded_at} /> : null;
    }

    return row[columnKey];
  };
  const containerHeight = showingAnyBlock
    ? "calc(100vh - 705px)"
    : "calc(100vh - 635px)";
  return (
    <div
      className={styles[`${cssClassName}__container`]}
      style={{ height: containerHeight }}
    >
      {sortedRows.length === 0 ? (
        <div className={styles[`${cssClassName}__empty-wrapper`]}>
          {emptyStateMap[activeOTAHistoryTab]}
        </div>
      ) : (
        <div className={styles[`${cssClassName}__wrapper`]}>
          <div className={styles[`${cssClassName}__scroll`]}>
            <table ref={tableRef} className={styles[`${cssClassName}__table`]}>
              <OTAHistoryTableHeader
                columns={columns}
                columnWidths={columnWidths}
                resizing={resizing}
                handleMouseDown={handleMouseDown}
                handleSort={handleSort}
                sortConfig={sortConfig}
              />
              <tbody>
                {sortedRows.map((row) => (
                  <tr
                    key={row?.firmware_id}
                    className={styles[`${cssClassName}__row`]}
                    onClick={() => handleRowClick(row)}
                  >
                    {columns.map((column, index) => (
                      <td
                        key={column.key}
                        style={{
                          width: `${columnWidths[column.key]}%`,
                        }}
                        className={styles[`${cssClassName}__cell`]}
                      >
                        <div
                          className={`${
                            styles[`${cssClassName}__cell-content`]
                          } ${
                            column.key === "filename"
                              ? styles[`${cssClassName}__cell--primary`]
                              : styles[`${cssClassName}__cell--secondary`]
                          }`}
                        >
                          {renderCellContent(column.key, row)}
                        </div>
                        {index < columns.length - 1 && (
                          <div
                            className={
                              styles[`${cssClassName}__cell-separator`]
                            }
                          />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
